// API client mirroring the §11 envelope. Hand-rolled (no codegen) so the
// demo isn't blocked on tooling.
//
// VITE_API_BASE_URL drives the absolute URL prefix at build time. When unset
// (e.g. `npm run dev`), paths stay relative and Vite's proxy
// (vite.config.ts) forwards them to the local backend on :8000.

import { fetchEventSource } from '@microsoft/fetch-event-source';

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';
const AUTH_STORAGE_KEY = 'searchx_auth_token';

function url(path: string): string {
  return `${BASE_URL}${path}`;
}

/** Get the current auth token from localStorage (if any). */
function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEY);
}

/** Build standard headers including auth when available. */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export type EntityType = 'COMPANY' | 'PERSON';
export type EntitySubtype =
  | 'LEGAL_ENTITY'
  | 'NATURAL_PERSON'
  | 'SOLE_PROPRIETOR'
  | 'OFFICER';
export type SearchMode = 'QUICK' | 'DEEP';
export type Jurisdiction = string; // ISO-3166-1 alpha-2; the live set comes from /metadata.

export interface Identifier {
  scheme: string;          // e.g. de_handelsregister, pl_nip, northdata_url
  value: string;           // canonical value -- includes the disambiguator for compound schemes
  label: string;           // human-readable label for the UI
  components: Record<string, string>;
  priority: number;
  dedupe: boolean;         // false = source link / unverified; never used as primary in row view
}

export interface InferredField {
  field: string;                     // canonical field path, e.g. "entity_subtype"
  value: unknown;                    // open-typed; depends on the field
  confidence: number;                // self-reported, on [0, 1]
  reasoning: string;                 // human-readable justification
  source_evidence: string[];         // pointers back into the visible entity data
}

export interface StructuredAddress {
  country?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  street?: string | null;
}

export type PartyKind = 'PERSON' | 'ORGANISATION';

export interface RelatedParty {
  name: string;
  party_kind: PartyKind;
  role_canonical: string;
  role_label: string;
  date_of_birth?: string | null;
  city?: string | null;
  address?: StructuredAddress | null;
}

export type OwnershipSubjectType = 'ENTITY' | 'PERSON' | 'UNKNOWN';

export type OwnershipInterestType =
  | 'shareholding'
  | 'votingRights'
  | 'appointmentOfBoard'
  | 'seniorManagingOfficial'
  | 'controlViaCompanyRulesOrArticles'
  | 'otherInfluenceOrControl'
  | 'accountingConsolidationParent'
  | 'subsidiary'
  | 'unknown';

export interface OwnershipParty {
  name: string;
  party_type: OwnershipSubjectType;
  jurisdiction?: Jurisdiction | null;
  identifiers: Identifier[];
  address?: StructuredAddress | null;
  source_ref?: string | null;
}

// A directed control edge: `interested_party` controls `subject`. Assurance and
// confidence are independent — a GLEIF parent is POTENTIAL (accounting-
// consolidation signal) until a primary register confirms it.
export interface OwnershipInterest {
  subject: OwnershipParty;
  interested_party: OwnershipParty;
  interest_type: OwnershipInterestType;
  direct_or_indirect: 'direct' | 'indirect' | 'unknown';
  // Percentage band: *_pct is the lower bound (threshold), *_pct_max the upper
  // bound (null when the source gives none). min == max means an exact figure.
  share_pct?: number | null;
  share_pct_max?: number | null;
  voting_pct?: number | null;
  voting_pct_max?: number | null;
  is_controlling?: boolean | null;
  is_beneficial_owner: boolean;
  // Lifecycle: false ⇒ the relationship has ended (historic). Authoritative
  // current-vs-historic signal, independent of ended_date (which may be null
  // for a ceased edge when the source gives no date). Absent ⇒ current.
  is_current?: boolean;
  statement_date?: string | null;
  // The cease date, when the source provides one.
  ended_date?: string | null;
  source: string;
  source_url?: string | null;
  assurance: Assurance;
  confidence: number;
  evidence: string[];
  raw: Record<string, unknown>;
}

// Authoritative, standardized registry detail (GOV_PRIMARY). Rendered without
// the AI disclaimer — distinct from `inferred`.
export interface CompanyDetail {
  legal_form?: string | null;
  legal_form_code?: string | null;
  archetype?: string | null;
  address?: StructuredAddress | null;
  incorporation_date?: string | null;
  status?: string | null;
  parties: RelatedParty[];
  ownership: OwnershipInterest[];
  provenance: string;
}

export interface CanonicalEntity {
  source: string;
  source_id: string;
  entity_type: EntityType;
  entity_subtype: EntitySubtype | null;
  name: string;
  jurisdiction: Jurisdiction;
  identifiers: Identifier[];
  addresses: Array<Record<string, unknown>>;
  roles: Array<Record<string, unknown>>;
  legal_form?: string | null;
  address?: StructuredAddress | null;
  detail?: CompanyDetail | null;     // authoritative registry detail
  raw: Record<string, unknown>;
  confidence: number;
  fetched_at: string;
  possible_duplicate: boolean;
  result_token: string;
  inferred: InferredField[];         // AI-inferred annotations
}

export interface SearchMeta {
  sources_queried: number;
  sources_responded: number;
  sources_responded_names: string[];
  sources_timed_out: string[];
  sources_errored: string[];
  from_cache: boolean;
  latency_ms: number;
  audit_recorded: boolean;
  adk_processed: boolean;
  enrichment_available: boolean;
}

export interface SearchResponse {
  search_id: string;
  query: {
    term: string;
    entity_type: EntityType;
    jurisdiction: Jurisdiction;
    mode: SearchMode;
    requester_id: string;
    correlation_id: string;
    limit: number;
    filters: Record<string, unknown>;
  };
  results: CanonicalEntity[];
  meta: SearchMeta;
}

export interface SearchRequestBody {
  term: string;
  entity_type: EntityType;
  jurisdiction: Jurisdiction;
  region?: string | null;            // ISO 3166-2 subdivision, e.g. "US-CA"
  requester_id: string;
  limit?: number;
  filters?: Record<string, unknown>;
}

export interface JurisdictionOption {
  code: Jurisdiction;
  name: string;
}

export interface SubdivisionOption {
  code: string;     // ISO 3166-2, e.g. "US-CA"
  name: string;
  type: string;     // "State", "Province", "Territory"
}

export interface JurisdictionEntry {
  code: string;                          // ISO 3166-1 or "*" wildcard
  name: string;
  has_adapter: boolean;                  // false => web-only
  subdivisions: SubdivisionOption[];
}

export interface Metadata {
  jurisdictions: JurisdictionOption[];   // legacy: adapter-supported only
  entity_types: EntityType[];
  availability: Record<EntityType, Jurisdiction[]>;
  all_jurisdictions: JurisdictionEntry[]; // pycountry-backed; includes "*" wildcard
}

export interface UserPreferences {
  entity_type?: EntityType | null;
  jurisdiction?: Jurisdiction | null;
  region?: string | null;
  mode?: SearchMode | null;
  coverage_mode?: 'proven' | 'all' | null;
  continent?: 'ALL' | 'EU' | 'NA' | 'SA' | 'AS' | 'AF' | 'OC' | null;
  theme?: 'light' | 'dark' | null;
}

export async function fetchMetadata(): Promise<Metadata> {
  const resp = await fetch(url('/metadata'), { headers: authHeaders() });
  if (!resp.ok) {
    throw new Error(`metadata failed: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as Metadata;
}

export interface Source {
  name: string;
  display_name: string;
  tier: string;
  is_blocking: boolean;
  supported_jurisdictions: string[];
  supported_entity_types: string[];
  cache_ttl_seconds: number;
  note: string | null;
}

export async function fetchSources(): Promise<Source[]> {
  const resp = await fetch(url('/sources'), { headers: authHeaders() });
  if (!resp.ok) {
    throw new Error(`sources failed: ${resp.status} ${resp.statusText}`);
  }
  const body = (await resp.json()) as { sources: Source[] };
  return body.sources;
}

export async function fetchPreferences(requesterId: string): Promise<UserPreferences> {
  const resp = await fetch(url('/preferences'), {
    headers: { ...authHeaders(), 'X-Requester-Id': requesterId },
  });
  if (!resp.ok) {
    throw new Error(`preferences failed: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as UserPreferences;
}

export async function savePreferences(
  requesterId: string,
  body: UserPreferences,
): Promise<UserPreferences> {
  const resp = await fetch(url('/preferences'), {
    method: 'PUT',
    headers: { ...authHeaders(), 'X-Requester-Id': requesterId },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new Error(`preferences save failed: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as UserPreferences;
}

export async function search(
  mode: SearchMode,
  body: SearchRequestBody,
): Promise<SearchResponse> {
  const path = mode === 'QUICK' ? '/search/quick' : '/search/deep';
  const resp = await fetch(url(path), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new Error(`search failed: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as SearchResponse;
}

// ---- DEEP streaming variant -----------------------------------------------
//
// Native EventSource only supports GET, but /search/deep/stream takes a JSON
// body. @microsoft/fetch-event-source wraps `fetch` so we can POST and still
// get back named SSE events.

export interface ProgressEvent {
  stage: string;          // 'analyzing_query', 'searching_<source>', 'processing_results', 'complete', 'fallback'
  message: string;
  elapsed_ms: number;
}

export interface SearchDeepStreamCallbacks {
  onProgress: (event: ProgressEvent) => void;
  onResult: (response: SearchResponse) => void;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

export async function searchDeepStream(
  body: SearchRequestBody,
  { onProgress, onResult, onError, signal }: SearchDeepStreamCallbacks,
): Promise<void> {
  let receivedResult = false;
  let streamError: Error | null = null;

  await fetchEventSource(url('/search/deep/stream'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
    // The lib reconnects by default; for a one-shot stream we don't want
    // to retry on close -- our backend sends a 'complete' event then ends.
    openWhenHidden: true,
    onmessage(ev) {
      try {
        const data = JSON.parse(ev.data);
        if (ev.event === 'progress') {
          onProgress(data as ProgressEvent);
        } else if (ev.event === 'result') {
          receivedResult = true;
          onResult(data as SearchResponse);
        }
      } catch (e) {
        // Malformed event — surface but don't break the stream.
        streamError = e instanceof Error ? e : new Error(String(e));
      }
    },
    onclose() {
      if (!receivedResult && !streamError) {
        streamError = new Error('stream closed before result');
      }
    },
    onerror(err) {
      streamError = err instanceof Error ? err : new Error(String(err));
      // Throwing from onerror disables the lib's automatic retry.
      throw streamError;
    },
  });

  if (streamError && onError) {
    onError(streamError);
  } else if (streamError) {
    throw streamError;
  }
}

// ---- Entity enrichment streaming -----------------------------------------
//
// /search/enrich/stream runs a Vertex grounded search + synthesis + validators
// pipeline for one previously-returned entity. It emits progress events, then
// one `field` event per validated InferredField as soon as each one passes
// validation, then a terminal `result`. Mirrors searchDeepStream's structure.

export interface EnrichmentProgressEvent {
  stage: string;        // 'loading_entity' | 'searching_web' | 'fetching_pages'
                        // | 'synthesizing' | 'complete' | 'fallback'
  message: string;
  elapsed_ms: number;
}

export interface EnrichmentFieldEvent {
  field: InferredField;
}

// Assurance lifecycle: POTENTIAL (AI/web, wears the disclaimer) → VERIFIED
// (authoritative: VIES/GLEIF/registry) | REFUTED | CONFLICTING.
export type Assurance = 'POTENTIAL' | 'VERIFIED' | 'REFUTED' | 'CONFLICTING';

export interface EnrichedFact {
  field: string;
  value: unknown;
  assurance: Assurance;
  confidence: number;
  sources: string[];
  verifier: string | null;
  evidence: string[];
  superseded_by?: string | null;
}

// Real-time fact transition: discovered → verifying → verified | refuted |
// conflicting | superseded.
export interface EnrichmentFactEvent {
  field: string;
  value: unknown;
  assurance: Assurance;
  transition: 'discovered' | 'verifying' | 'verified' | 'refuted' | 'conflicting' | 'superseded';
  confidence: number;
  source: string | null;
  verifier: string | null;
  evidence: string[];
}

export interface EnrichmentToolEvent {
  tool_id: string;
  kind: 'discovery' | 'verifier' | 'authoritative_fetch';
  status: 'started' | 'ok' | 'error';
  latency_ms: number;
  cost_units: number;
}

// Authoritative registry detail, streamed the moment a fetch tool returns it
// (ahead of the terminal result) so officers surface early.
export interface EnrichmentDetailEvent {
  detail: CompanyDetail;
}

// Ownership/control edges, streamed as a tool discovers them (the edge analogue
// of EnrichmentDetailEvent). Carries only the edges added in this step.
export interface EnrichmentOwnershipEvent {
  edges: OwnershipInterest[];
}

export interface EnrichmentResult {
  result_token: string;
  inferred: InferredField[];
  // Populated by the harness; empty for the legacy pipeline.
  facts?: EnrichedFact[];
  detail?: CompanyDetail | null;
  assurance_counts?: Record<string, number>;
  from_cache: boolean;
  generated_at: string;
  error?: string | null;
}

export interface EnrichmentStreamCallbacks {
  onProgress: (event: EnrichmentProgressEvent) => void;
  onField: (event: EnrichmentFieldEvent) => void;
  onResult: (result: EnrichmentResult) => void;
  // Optional harness callbacks; legacy callers ignore them.
  onFact?: (event: EnrichmentFactEvent) => void;
  onTool?: (event: EnrichmentToolEvent) => void;
  onDetail?: (event: EnrichmentDetailEvent) => void;
  onOwnership?: (event: EnrichmentOwnershipEvent) => void;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

export interface EnrichEntityArgs {
  result_token: string;
  search_id: string;
  force?: boolean;
  // The entity to enrich, sent so the backend need not rehydrate it from
  // Postgres. Lets enrichment work when the audit store is unavailable.
  entity: CanonicalEntity;
}

export async function enrichEntityStream(
  args: EnrichEntityArgs,
  { onProgress, onField, onResult, onFact, onTool, onDetail, onOwnership, onError, signal }: EnrichmentStreamCallbacks,
): Promise<void> {
  let receivedResult = false;
  let streamError: Error | null = null;

  await fetchEventSource(url('/search/enrich/stream'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      result_token: args.result_token,
      search_id: args.search_id,
      force: args.force ?? false,
      entity: args.entity,
    }),
    signal,
    openWhenHidden: true,
    onmessage(ev) {
      try {
        const data = JSON.parse(ev.data);
        if (ev.event === 'progress') {
          onProgress(data as EnrichmentProgressEvent);
        } else if (ev.event === 'field') {
          onField(data as EnrichmentFieldEvent);
        } else if (ev.event === 'fact') {
          onFact?.(data as EnrichmentFactEvent);
        } else if (ev.event === 'tool') {
          onTool?.(data as EnrichmentToolEvent);
        } else if (ev.event === 'detail') {
          onDetail?.(data as EnrichmentDetailEvent);
        } else if (ev.event === 'ownership') {
          onOwnership?.(data as EnrichmentOwnershipEvent);
        } else if (ev.event === 'result') {
          receivedResult = true;
          onResult(data as EnrichmentResult);
        }
      } catch (e) {
        streamError = e instanceof Error ? e : new Error(String(e));
      }
    },
    onclose() {
      if (!receivedResult && !streamError) {
        streamError = new Error('enrichment stream closed before result');
      }
    },
    onerror(err) {
      streamError = err instanceof Error ? err : new Error(String(err));
      throw streamError;
    },
  });

  if (streamError && onError) {
    onError(streamError);
  } else if (streamError) {
    throw streamError;
  }
}

// Click signal — fire-and-forget per PRD §9. `keepalive: true` so an
// in-flight POST survives a navigation away from the page.
export function recordClick(args: {
  result_token: string;
  search_id: string;
  position: number;
  requester_id: string;
}): void {
  fetch(url('/search/click'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(args),
    keepalive: true,
  }).catch((err: unknown) => {
    console.warn('[searchx] click signal failed:', err);
  });
}
